import { useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import { getToken } from "utils/getToken";
import useIsTablet from "hooks/useIsTablet";
import Joyride, { ACTIONS, CallBackProps, EVENTS, ORIGIN, STATUS } from "react-joyride";
import { useAuthStore } from "store/authStore";
import { useMobileOnboardingSteps, useDesktopOnboardingSteps } from "./OnboardingSteps";

interface IOnboardingProps {
  isMobileSidebarOpen: boolean;
  setMobileSidebarOpen: (status: boolean) => void;
}

const Onboarding = ({ isMobileSidebarOpen, setMobileSidebarOpen }: IOnboardingProps) => {
  const token = getToken();
  const { updateUserData } = useAuthStore();
  const [run, setRun] = useState<boolean>(true);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const isTablet = useIsTablet();
  const mobileSteps = useMobileOnboardingSteps();
  const desktopSteps = useDesktopOnboardingSteps();
  const steps = isTablet ? mobileSteps : desktopSteps;

  const { triggerFetch: updateUserOnboardingStatus } = useFetch({
    url: "profile/update-onboarding-status",
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const handleOnboardingStatusChange = async () => {
    const response = await updateUserOnboardingStatus();

    if (response.success) {
      updateUserData({ onboardingCompleted: true });
    }
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, origin, status, type, step } = data;

    if (
      step.target === '[data-tour="mobile-sidebar-open-button"]' &&
      type === EVENTS.STEP_AFTER &&
      action === ACTIONS.NEXT
    ) {
      setMobileSidebarOpen(true);
      return;
    }

    if (action === ACTIONS.PREV && step.target === '[data-tour="sidebar"]' && type === EVENTS.STEP_AFTER) {
      setMobileSidebarOpen(false);
      setStepIndex((prev) => prev - 1);
      return;
    }

    if (type === EVENTS.STEP_AFTER && status === STATUS.RUNNING) {
      setStepIndex((prev) => prev + (action === ACTIONS.PREV ? -1 : 1));
    }

    if (action === ACTIONS.CLOSE && origin === ORIGIN.OVERLAY) {
      setRun(false);
      void handleOnboardingStatusChange();
      return;
    }

    if (status === STATUS.SKIPPED || status === STATUS.FINISHED) {
      setMobileSidebarOpen(false);
      setRun(false);
      void handleOnboardingStatusChange();
      return;
    }
  };

  useEffect(() => {
    if (!isMobileSidebarOpen) {
      return;
    }

    const timeout = setTimeout(() => {
      setStepIndex((prev) => prev + 1);
    }, 300);

    return () => clearTimeout(timeout);
  }, [isMobileSidebarOpen]);

  return (
    <Joyride
      run={run}
      steps={steps}
      callback={handleJoyrideCallback}
      continuous
      stepIndex={stepIndex}
      hideCloseButton
      showSkipButton
      styles={{
        options: { zIndex: 100000 },
        tooltip: {
          backgroundColor: "#FFFFFF",
          borderRadius: 8,
          color: "#2E263D",
          fontSize: 14,
          width: "400px",
          maxWidth: "100%",
          padding: 24,
          position: "relative",
        },
        tooltipContainer: {
          lineHeight: 1.4,
          textAlign: "left",
        },
        tooltipTitle: {
          fontSize: 16,
          fontWeight: 500,
          marginBottom: "16px",
        },
        tooltipContent: {
          padding: 0,
        },
        tooltipFooter: {
          alignItems: "center",
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 18,
        },
        buttonNext: {
          width: "max=content",
          height: "36px",
          marginLeft: "auto",
          marginRight: 5,
          color: "#FFF",
          border: 0,
          borderRadius: "8px",
          backgroundColor: "#F4701B",
          cursor: "pointer",
          fontSize: 14,
          padding: "16px 24px",
          WebkitAppearance: "none",
        },
        buttonBack: {
          width: "max=content",
          height: "36px",
          backgroundColor: "transparent",
          border: "1px solid #C9D0D8",
          borderRadius: "8px",
          color: "#6D6777",
          cursor: "pointer",
          fontSize: 14,
          padding: "16px 24px",
          WebkitAppearance: "none",
        },
        buttonSkip: {
          width: "max=content",
          height: "36px",
          backgroundColor: "transparent",
          border: 0,
          color: "#F4701B",
          cursor: "pointer",
          fontSize: 14,
          padding: "16px 0px",
          WebkitAppearance: "none",
        },
        spotlight: {
          borderRadius: 8,
          position: "absolute",
          backgroundColor: "gray",
        },
      }}
    />
  );
};

export default Onboarding;
